/**
 * Reference code utilities for payment matching
 *
 * Reference codes are human-readable identifiers used to match
 * real-world payments (bank transfers) to payment instructions.
 *
 * Format: SP + 6 characters from safe alphabet
 * Example: SP8F3K2M
 *
 * Safe alphabet excludes confusing characters:
 * - No 0/O (zero vs letter O)
 * - No 1/I/L (one vs I vs L)
 *
 * Keyspace: 31^6 ≈ 887 million unique codes.
 * First birthday collision unlikely before ~30 thousand codes at usage volume.
 */

// Safe alphabet: uppercase letters + digits, excluding confusing chars
// Removed: 0, 1, I, L, O
const SAFE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const REFERENCE_CODE_PREFIX = 'SP';
const REFERENCE_CODE_LENGTH = 6; // Characters after prefix

/** Every generated code is exactly this long, so callers can budget space for one before it exists. */
export const REFERENCE_CODE_TOTAL_LENGTH = REFERENCE_CODE_PREFIX.length + REFERENCE_CODE_LENGTH;
// Token length range accepted by findPartialAnagramMatch/hasPartialAnagramCandidateToken —
// one character short of the full body is the floor agreed for this fallback (shorter
// tokens match too many candidates by chance); the full body length is the ceiling.
const PARTIAL_MATCH_MIN_TOKEN_LENGTH = REFERENCE_CODE_LENGTH - 1;
const PARTIAL_MATCH_MAX_TOKEN_LENGTH = REFERENCE_CODE_LENGTH;

/**
 * Cryptographically random integer in [0, max).
 * Uses the Web Crypto API (available in both browsers and Node.js 15+).
 * A tiny modulo bias exists for large `max` values but is negligible for our alphabet size (31).
 */
export function secureRandomIndex(max: number): number {
  const bytes = new Uint32Array(1);
  // globalThis.crypto is available in Node.js 19+ and all modern browsers
  globalThis.crypto.getRandomValues(bytes);
  return bytes[0]! % max;
}

/**
 * Generate a cryptographically random reference code.
 * Format: SP + 6 chars = 8 chars total.
 *
 * @example
 * generateReferenceCode() // => "SP8F3K2M"
 */
export function generateReferenceCode(): string {
  const randomPart = Array.from(
    {length: REFERENCE_CODE_LENGTH},
    () => SAFE_ALPHABET[secureRandomIndex(SAFE_ALPHABET.length)]
  ).join('');

  return `${REFERENCE_CODE_PREFIX}${randomPart}`;
}

/**
 * Normalize a reference code for case-insensitive matching.
 * - Converts to uppercase
 * - Removes whitespace and common separators
 * - Handles common OCR/human errors (0→O, 1→I, etc.)
 *
 * @example
 * normalizeReferenceCode("sp8f3k2m7q") // => "SP8F3K2M7Q"
 * normalizeReferenceCode("SP-8F3K-2M7Q") // => "SP8F3K2M7Q"
 */
export function normalizeReferenceCode(code: string): string {
  return code
    .toUpperCase()
    .replace(/[\s\-_.]/g, '') // Remove whitespace and common separators
    .replace(/0/g, 'O') // Common OCR error: 0 → O
    .replace(/1/g, 'I'); // Common OCR error: 1 → I
}

/**
 * Validate if a string looks like a valid reference code.
 *
 * @example
 * isValidReferenceCode("SP8F3K2M") // => true
 * isValidReferenceCode("invalid") // => false
 */
export function isValidReferenceCode(code: string): boolean {
  const normalized = normalizeReferenceCode(code);
  // Must start with SP and have correct length
  if (!normalized.startsWith(REFERENCE_CODE_PREFIX)) {
    return false;
  }
  if (normalized.length !== REFERENCE_CODE_PREFIX.length + REFERENCE_CODE_LENGTH) {
    return false;
  }
  // Rest must be from safe alphabet (after normalization, 0→O, 1→I already applied)
  const rest = normalized.slice(REFERENCE_CODE_PREFIX.length);
  return rest.split('').every(char => SAFE_ALPHABET.includes(char) || char === 'O' || char === 'I');
}

/**
 * Extract reference codes from a bank narrative/description.
 * Banks often include reference codes in payment descriptions.
 * Matches the current reference-code format (`SP` + 6 chars from the safe alphabet).
 *
 * @example
 * extractReferenceCode("Payment for SP8F3K2M unit 5")
 * // => ["SP8F3K2M"]
 *
 * extractReferenceCode("REF: SP-8F3-K2M")
 * // => ["SP8F3K2M"]
 */
export function extractReferenceCode(narrative: string): string[] {
  // Match SP followed by alphanumeric chars (with optional separators).
  // Pattern accounts for various formats banks might use; the final length/alphabet
  // check is enforced by isValidReferenceCode below.
  const patterns = [
    // Exact 6-char format: SP followed by 6 alphanumeric chars from safe alphabet
    /(?:^|[^A-Za-z])SP[A-HJ-NP-Z2-9]{6}(?:[^A-Za-z0-9]|$)/gi,
    // With separators, 6-char: SP-XXX-XXX, SP.XXX.XXX, SP XXX XXX, or any mix/repeat of these
    /(?:^|[^A-Za-z])SP[-._\s]*[A-HJ-NP-Z0-9]{3}[-._\s]*[A-HJ-NP-Z0-9]{3}(?:[^A-Za-z0-9]|$)/gi
  ];

  const matches = new Set<string>();

  for (const pattern of patterns) {
    const found = narrative.match(pattern);
    if (found) {
      for (const match of found) {
        // Extract just the code part (remove any leading/trailing non-code chars)
        const cleaned = match.replace(/^[^A-Za-z]+/, '').replace(/[^A-Za-z0-9._-]+$/, '');
        const normalized = normalizeReferenceCode(cleaned);
        if (isValidReferenceCode(normalized)) {
          matches.add(normalized);
        }
      }
    }
  }

  return Array.from(matches);
}

/**
 * Recover a reference code that survived intact but was character-reordered by
 * upstream narrative corruption (observed from Mizrahi-originated Open Finance
 * transactions — the same 8 characters, scrambled, rather than missing or
 * truncated). Splits the narrative into whitespace-delimited tokens, keeps
 * 8-character tokens whose characters are all in the safe alphabet, and compares
 * each token's sorted-character signature against the candidate codes' signatures.
 * Returns the candidate only when exactly one distinct candidate matches — ambiguous
 * or absent matches return null rather than guessing.
 *
 * @example
 * findAnagramMatch('זיכוי מהמזרחי 283FKMSP תשלום', ['SP8F3K2M'])
 * // => 'SP8F3K2M'
 */
export function findAnagramMatch(narrative: string, candidateCodes: string[]): string | null {
  const codeLength = REFERENCE_CODE_PREFIX.length + REFERENCE_CODE_LENGTH;
  return findScrambledMatch(narrative, candidateCodes, {
    minLength: codeLength,
    maxLength: codeLength,
    isMatch: (token, body) => sortChars(token) === sortChars(REFERENCE_CODE_PREFIX + body)
  });
}

/**
 * Cheap check for whether a narrative could possibly contain an anagram-scrambled
 * reference code — a whitespace-delimited token of exactly the right length. Callers
 * use this to skip fetching the anagram candidate pool (a DB round trip) for the
 * common case where a narrative has no code in any form, without duplicating
 * `findAnagramMatch`'s own tokenizing logic.
 */
export function hasAnagramCandidateToken(narrative: string): boolean {
  const codeLength = REFERENCE_CODE_PREFIX.length + REFERENCE_CODE_LENGTH;
  return hasScrambledCandidateToken(narrative, codeLength, codeLength);
}

/**
 * Recover a reference code that arrived both scrambled AND missing 1
 * character — observed from Leumi-originated Open Finance transactions,
 * where the narrative token is 5-6 characters instead of the full 6-character
 * body, but every surviving character is a genuine (possibly reordered)
 * subset of the real code. Unlike findAnagramMatch, this checks whether the
 * token's characters are a SUBSET of a candidate's body (allowing 0-1
 * characters to be missing), not an exact-length anagram.
 *
 * Also covers a second corruption shape, observed from Discount: the "SP"
 * prefix survives intact (unlike Leumi, where it's stripped before the body
 * is scrambled) while the body itself is truncated by 1 character. A token 1-2
 * characters longer that still starts with "SP" is tried with the prefix
 * stripped, against the same 0-1-missing tolerance.
 *
 * Because dropping characters increases the chance of a coincidental match,
 * a result from this function is lower-confidence than findAnagramMatch —
 * callers should route it to manual review rather than auto-settling.
 * Returns the candidate only when exactly one distinct candidate matches —
 * ambiguous or absent matches return null rather than guessing.
 *
 * @example
 * findPartialAnagramMatch('זיכוי מלאומי תשלום מקדמה h7kam מח-ן:002208396', ['SPMSH7KA'])
 * // => 'SPMSH7KA'
 */
export function findPartialAnagramMatch(
  narrative: string,
  candidateCodes: string[]
): string | null {
  return findScrambledMatch(narrative, candidateCodes, {
    minLength: PARTIAL_MATCH_MIN_TOKEN_LENGTH,
    maxLength: PARTIAL_MATCH_MAX_TOKEN_LENGTH,
    isMatch: isSubMultiset,
    allowPrefixedToken: true
  });
}

/**
 * Cheap check for whether a narrative could possibly contain a partial-anagram
 * token — either a bare 5-6 character body (see findPartialAnagramMatch) or a
 * 7-8 character token that still carries the "SP" prefix. Mirrors
 * hasAnagramCandidateToken's role: lets callers skip fetching the candidate
 * pool when no token of a plausible length exists at all.
 */
export function hasPartialAnagramCandidateToken(narrative: string): boolean {
  if (
    hasScrambledCandidateToken(
      narrative,
      PARTIAL_MATCH_MIN_TOKEN_LENGTH,
      PARTIAL_MATCH_MAX_TOKEN_LENGTH
    )
  ) {
    return true;
  }
  return splitNarrativeTokens(narrative).some(token =>
    isPrefixedToken(
      token,
      PARTIAL_MATCH_MIN_TOKEN_LENGTH + REFERENCE_CODE_PREFIX.length,
      PARTIAL_MATCH_MAX_TOKEN_LENGTH + REFERENCE_CODE_PREFIX.length
    )
  );
}

/**
 * Shared skeleton behind findAnagramMatch and findPartialAnagramMatch: both recover a
 * reference code from a corrupted narrative token by tokenizing on whitespace/comma/slash,
 * filtering to a token-length window and the safe alphabet, then testing each surviving
 * token against every outstanding candidate's body (the code with its "SP" prefix
 * stripped). The two fallbacks differ only in which tokens are plausible (full-length vs.
 * shortened-by-truncation) and what counts as a match (exact reorder vs. reorder with
 * characters missing) — both expressed via `options`.
 *
 * When `allowPrefixedToken` is set, a token 1-2 characters longer that still starts with
 * "SP" is also tried with the prefix stripped — the corrupted narrative may keep the
 * literal prefix instead of stripping it (see findPartialAnagramMatch).
 *
 * Returns the candidate only when exactly one distinct candidate matches across the
 * whole narrative — ambiguous or absent matches return null rather than guessing.
 */
function findScrambledMatch(
  narrative: string,
  candidateCodes: string[],
  options: {
    minLength: number;
    maxLength: number;
    isMatch: (token: string, candidateBody: string) => boolean;
    allowPrefixedToken?: boolean;
  }
): string | null {
  const codeLength = REFERENCE_CODE_PREFIX.length + REFERENCE_CODE_LENGTH;

  const candidateBodies = new Set<string>();
  for (const candidate of candidateCodes) {
    const normalized = normalizeReferenceCode(candidate);
    if (normalized.length === codeLength && normalized.startsWith(REFERENCE_CODE_PREFIX)) {
      candidateBodies.add(normalized.slice(REFERENCE_CODE_PREFIX.length));
    }
  }
  if (candidateBodies.size === 0) return null;

  const matches = new Set<string>();
  for (const token of splitNarrativeTokens(narrative)) {
    for (const normalized of candidateBodyTokens(token, options)) {
      if (!isSafeAlphabetOnly(normalized)) continue;

      for (const body of candidateBodies) {
        if (options.isMatch(normalized, body)) {
          matches.add(REFERENCE_CODE_PREFIX + body);
        }
      }
    }
  }

  return matches.size === 1 ? [...matches][0]! : null;
}

/**
 * The candidate body string(s) a single narrative token could represent: the token
 * itself when it's within the plain length window, plus the prefix-stripped token when
 * `allowPrefixedToken` is set and the token still carries a literal "SP".
 */
function candidateBodyTokens(
  token: string,
  options: {minLength: number; maxLength: number; allowPrefixedToken?: boolean}
): string[] {
  const candidates: string[] = [];
  if (token.length >= options.minLength && token.length <= options.maxLength) {
    candidates.push(normalizeReferenceCode(token));
  }
  if (
    options.allowPrefixedToken &&
    isPrefixedToken(
      token,
      options.minLength + REFERENCE_CODE_PREFIX.length,
      options.maxLength + REFERENCE_CODE_PREFIX.length
    )
  ) {
    candidates.push(normalizeReferenceCode(token).slice(REFERENCE_CODE_PREFIX.length));
  }
  return candidates;
}

/** Whether `token` is within [minLength, maxLength] and still starts with the literal "SP" prefix. */
function isPrefixedToken(token: string, minLength: number, maxLength: number): boolean {
  return (
    token.length >= minLength &&
    token.length <= maxLength &&
    normalizeReferenceCode(token).startsWith(REFERENCE_CODE_PREFIX)
  );
}

/** Splits a narrative into candidate tokens on whitespace, commas, and slashes. */
function splitNarrativeTokens(narrative: string): string[] {
  return narrative.split(/[\s,/]+/).filter(token => token.length > 0);
}

function hasScrambledCandidateToken(
  narrative: string,
  minLength: number,
  maxLength: number
): boolean {
  return splitNarrativeTokens(narrative).some(
    token => token.length >= minLength && token.length <= maxLength
  );
}

function sortChars(value: string): string {
  return value.split('').sort().join('');
}

function isSafeAlphabetOnly(value: string): boolean {
  return value
    .split('')
    .every(char => SAFE_ALPHABET.includes(char) || char === 'O' || char === 'I');
}

/**
 * Whether every character in `sub` can be matched to a distinct character in
 * `full` — i.e. `sub` is reconstructable by dropping characters from `full`
 * (in any order). Used to check whether a corrupted narrative token could be
 * the survivor of a candidate code with a few characters missing.
 */
function isSubMultiset(sub: string, full: string): boolean {
  const remaining = full.split('');
  for (const ch of sub) {
    const idx = remaining.indexOf(ch);
    if (idx === -1) return false;
    remaining.splice(idx, 1);
  }
  return true;
}

/**
 * Format a reference code for human display.
 * Inserts hyphens to improve readability: SP8F3K2M → SP-8F3-K2M
 *
 * Only formats at display time — never use for matching or bank transfer descriptions.
 */
export function formatReferenceCode(code: string): string {
  // Strip existing hyphens so an already-formatted code formats to itself.
  const raw = code.replace(/-/g, '');
  const prefix = raw.slice(0, 2);
  const body = raw.slice(2);
  if (body.length < 4) return raw;

  // Groups of 4 when body divides evenly: body=8→2, body=12→3, body=16→4
  if (body.length % 4 === 0) {
    const groups: string[] = [];
    for (let i = 0; i < body.length; i += 4) {
      groups.push(body.slice(i, i + 4));
    }
    return `${prefix}-${groups.join('-')}`;
  }

  // Two equal halves for even bodies not divisible by 4: body=6→3+3, body=10→5+5
  if (body.length % 2 === 0) {
    const half = body.length / 2;
    return `${prefix}-${body.slice(0, half)}-${body.slice(half)}`;
  }

  return raw;
}
